import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { useLang } from '../lib/lang';
import { IMAGES } from '../content/constants';
import { Page, LineReveal, Reveal, Overline } from '../components/motion';
import { CTASection, PartnerLogo } from '../components/blocks';
import { motion } from 'framer-motion';

const slugOf = (n) => n.toLowerCase().replace(/\s+/g, '-');

export default function PartnerPage() {
  const { slug } = useParams();
  const { t, link } = useLang();
  const data = t.partnerPages?.[slug];
  if (!data) return <Navigate to={link('partners')} replace />;

  const idx = t.partners.items.findIndex((p) => slugOf(p.name) === slug);

  return (
    <Page meta={{ ...data.meta, lang: t.lang }}>
      <section data-testid="partner-hero" className="relative overflow-hidden border-b border-white/10 blueprint">
        <div className="absolute inset-0 spotlight" aria-hidden="true" />
        <div className="absolute inset-0" aria-hidden="true">
          <img src={IMAGES[data.image]} alt="" className="w-full h-full object-cover opacity-[0.14] grayscale" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/40 to-obsidian" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-40 md:pt-52 pb-20 md:pb-28">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
            <Link
              to={link('partners')}
              data-testid="partner-back-link"
              className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400 hover:text-volt transition-colors duration-300 mb-10 border border-white/15 hover:border-volt px-4 py-2.5"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t.partners.hero.overline}
            </Link>
            <PartnerLogo slug={slug} name={data.name} className="w-full max-w-[280px] aspect-[3/2] rounded mb-8" />
            <Overline>{data.tag}</Overline>
          </motion.div>
          <LineReveal
            lines={[data.name]}
            className="mt-8 font-display font-black uppercase tracking-tighter leading-[0.95] text-white text-[clamp(3rem,9vw,8rem)]"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed"
          >
            {data.intro}
          </motion.p>
        </div>
      </section>

      <section data-testid="partner-families" className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid md:grid-cols-2 border-t border-l border-white/10">
            {data.families.map((f, i) => (
              <Reveal key={i} delay={i * 0.06} className="group border-r border-b border-white/10 p-8 md:p-12 hover:bg-surface transition-colors duration-500">
                <span className="font-mono text-xs text-volt tracking-[0.2em]">{String(i + 1).padStart(2, '0')}</span>
                <h2 className="mt-4 font-display text-2xl md:text-3xl font-bold tracking-tight text-white group-hover:text-volt transition-colors duration-500">
                  {f.t}
                </h2>
                <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{f.d}</p>
                <ul className="mt-6 space-y-2.5">
                  {f.items.map((item, j) => (
                    <li key={j} className="flex gap-3 font-mono text-xs text-zinc-400">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-volt" strokeWidth={3} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="partner-others" className="border-t border-white/10 bg-surface/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <Overline>{t.partners.overline}</Overline>
          <div className="mt-8 flex flex-wrap gap-3">
            {t.partners.items.map((p) => {
              const pslug = slugOf(p.name);
              const active = pslug === slug;
              return (
                <Link
                  key={p.name}
                  to={link('partner', undefined, pslug)}
                  data-testid={`partner-chip-${pslug}`}
                  className={`px-5 py-2.5 border font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    active ? 'border-volt bg-volt text-black font-bold' : 'border-white/15 text-zinc-300 hover:border-volt hover:text-volt'
                  }`}
                >
                  {p.name}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection title={t.partners.cta} btn={t.partners.cta} />
    </Page>
  );
}
